import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ClerkProvider, SignedIn, SignedOut, RedirectToSignIn } from "@clerk/clerk-react";
import Index from "./pages/Index";
import PublicationsPage from "./pages/Publications";
import TherapistsPage from "./pages/Therapists";
import BookOfComfort from "./pages/BookOfComfort";
import BookingTerms from "./pages/BookingTerms";
import AdminDashboard from "./pages/AdminDashboard";
import NotFound from "./pages/NotFound";
import { useAnalytics } from "./hooks/useAnalytics";
import { useDocumentTitle } from "./hooks/useDocumentTitle";

const queryClient = new QueryClient();

// Get Clerk publishable key from environment
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
  throw new Error("Missing Clerk Publishable Key");
}

const AppContent = () => {
  const location = useLocation();

  useAnalytics(); // Track pageviews
  useDocumentTitle(); // Update document title based on language

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  return null;
};

const App = () => (
  <ClerkProvider publishableKey={clerkPubKey}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppContent />
          <Routes>
            {/* Public routes - no authentication required */}
            <Route path="/" element={<Index />} />
            <Route path="/publications" element={<PublicationsPage />} />
            <Route path="/therapists" element={<TherapistsPage />} />
            <Route path="/bookofcomfort" element={<BookOfComfort />} />
            <Route path="/booking-terms" element={<BookingTerms />} />

            {/* Admin route with Clerk authentication */}
            <Route
              path="/admin"
              element={
                <>
                  <SignedOut>
                    <RedirectToSignIn />
                  </SignedOut>
                  <SignedIn>
                    <AdminDashboard />
                  </SignedIn>
                </>
              }
            />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ClerkProvider>
);

export default App;
